/**
 * THROWAWAY DEMO CODE (Pillar procurement demo). Mutable sourcing demo
 * state: the events list, the RFP-2026-014 award flow, the negotiation run and
 * the RFQ-2026-022 decision. Agent progress is stored as a cursor and advanced
 * by module-level timers, so it keeps running when the user switches tabs or
 * leaves the page. State resets on reload or via resetSourcingDemo().
 */
import { createDemoStore } from '@/demo/core/create-demo-store';
import {
  NEGOTIATED_SAVINGS,
  RFP_ID,
  RFQ_ID,
  SEED_EVENTS,
} from './sourcing-data';
import {
  CONTRACT_DRAFT_DURATIONS,
  COUNTER_DURATIONS,
} from './sourcing-workflows';
import type { SourcingEvent } from './sourcing-types';

interface RfpState {
  awarded: boolean;
  /** -1 idle, 0..n-1 the running step, n all done. */
  contractCursor: number;
  unsuccessfulNotified: boolean;
  negotiationCursor: number;
}

interface RfqState {
  conditionalAward: boolean;
}

export interface SourcingState {
  events: SourcingEvent[];
  rfp: RfpState;
  rfq: RfqState;
}

function buildSeed(): SourcingState {
  return {
    events: SEED_EVENTS.map((event) => ({ ...event })),
    rfp: {
      awarded: false,
      contractCursor: -1,
      unsuccessfulNotified: false,
      negotiationCursor: -1,
    },
    rfq: { conditionalAward: false },
  };
}

export const sourcingStore = createDemoStore(buildSeed);

const timers = new Set<ReturnType<typeof setTimeout>>();

function clearTimers(): void {
  for (const timer of timers) clearTimeout(timer);
  timers.clear();
}

/** Advance a cursor 0..durations.length, holding on each index for its duration. */
function runCursor(
  durations: number[],
  setCursor: (cursor: number) => void,
): void {
  function step(index: number): void {
    setCursor(index);
    if (index >= durations.length) return;
    const timer = setTimeout(() => {
      timers.delete(timer);
      step(index + 1);
    }, durations[index]);
    timers.add(timer);
  }
  step(0);
}

export function resetSourcingDemo(): void {
  clearTimers();
  sourcingStore.reset();
}

/** Award RFP-2026-014 and start generating the contract drafts. */
export function awardRfp(): void {
  if (sourcingStore.getState().rfp.awarded) return;
  sourcingStore.update((draft) => {
    draft.rfp.awarded = true;
    const event = draft.events.find((e) => e.id === RFP_ID);
    if (event) {
      event.status = 'awarded';
      event.dateLabel = 'Awarded';
      event.dateOffsetDays = 0;
      event.statusNote = 'Contract drafting';
    }
  });
  runCursor(CONTRACT_DRAFT_DURATIONS, (cursor) => {
    sourcingStore.update((draft) => {
      draft.rfp.contractCursor = cursor;
    });
  });
}

export function markUnsuccessfulNotified(): void {
  sourcingStore.update((draft) => {
    draft.rfp.unsuccessfulNotified = true;
  });
}

/** Send the counter-offers and play the replies. Savings update at the end. */
export function startNegotiation(): void {
  if (sourcingStore.getState().rfp.negotiationCursor >= 0) return;
  runCursor(COUNTER_DURATIONS, (cursor) => {
    sourcingStore.update((draft) => {
      draft.rfp.negotiationCursor = cursor;
      if (cursor >= COUNTER_DURATIONS.length) {
        const event = draft.events.find((e) => e.id === RFP_ID);
        if (event) event.savings = NEGOTIATED_SAVINGS;
      }
    });
  });
}

export function approveConditionalAward(): void {
  sourcingStore.update((draft) => {
    draft.rfq.conditionalAward = true;
    const event = draft.events.find((e) => e.id === RFQ_ID);
    if (event) event.statusNote = 'Conditional award to Kestrel';
  });
}

/** Add an AI-drafted project to the top of the list. A second add of the same ID does nothing. */
export function addDraftEvent(event: SourcingEvent): void {
  sourcingStore.update((draft) => {
    if (draft.events.some((e) => e.id === event.id)) return;
    draft.events.unshift({ ...event });
  });
}

/** Send a drafted project to its invited suppliers. */
export function publishRfi(eventId: string): void {
  sourcingStore.update((draft) => {
    const event = draft.events.find((e) => e.id === eventId);
    if (event && event.status === 'draft') {
      event.status = 'published';
      event.dateLabel = 'Answers due';
      event.dateOffsetDays = 24;
      event.statusNote = 'Sent to invited suppliers';
    }
  });
}

export function useSourcingEvents(): SourcingEvent[] {
  return sourcingStore.useStore((state) => state.events);
}

export function useRfpState(): RfpState {
  return sourcingStore.useStore((state) => state.rfp);
}

export function useRfqState(): RfqState {
  return sourcingStore.useStore((state) => state.rfq);
}
